/**
 * GAME DATA STORE
 *
 * Singleton store for game data loaded from the backend.
 * This provides synchronous access to game data after initial load.
 *
 * Usage:
 * 1. GameDataProvider loads data from backend and calls setGameData()
 * 2. All other code can then import from this module synchronously
 */

// The actual game data - starts null until loaded from backend
let gameData = null;
let isLoaded = false;
let loadPromise = null;
let loadResolve = null;

// Create a promise that resolves when data is loaded
loadPromise = new Promise((resolve) => {
  loadResolve = resolve;
});

/**
 * Set game data (called by GameDataProvider after API fetch)
 */
export const setGameData = (data) => {
  gameData = data;
  isLoaded = true;
  if (loadResolve) {
    loadResolve(data);
  }
};

/**
 * Wait for game data to be loaded
 */
export const waitForGameData = () => loadPromise;

/**
 * Check if game data is loaded
 */
export const isGameDataLoaded = () => isLoaded;

/**
 * Get raw game data object
 */
export const getGameData = () => gameData;

// ============================================================================
// EXPORTED GETTERS - These provide synchronous access to game data
// They return undefined/empty if data isn't loaded yet
// ============================================================================

// Config
export const getICONS = () => gameData?.ICONS || {};
export const getEVOLUTION_CONFIG = () => gameData?.EVOLUTION_CONFIG || {};
export const getEVOLUTION_CHAINS = () => gameData?.EVOLUTION_CHAINS || {};
export const getGAME_CONFIG = () => gameData?.GAME_CONFIG || {};

// Pokemon
export const getPOKEMON = () => gameData?.POKEMON || {};
export const getLEGENDARY_POKEMON = () => gameData?.LEGENDARY_POKEMON || {};

// Moves
export const getMOVES = () => gameData?.MOVES || {};

// Gym/Elite Four
export const getGYM_LEADER_POKEMON = () => gameData?.GYM_LEADER_POKEMON || {};
export const getELITE_FOUR = () => gameData?.ELITE_FOUR || {};

// Support cards
export const getSUPPORT_CARDS = () => gameData?.SUPPORT_CARDS || {};
export const getLEGACY_SUPPORT_NAME_MAP = () => gameData?.LEGACY_SUPPORT_NAME_MAP || {};
export const getSUPPORT_LIMIT_BREAK_PROGRESSIONS = () => gameData?.SUPPORT_LIMIT_BREAK_PROGRESSIONS || {};
export const getSUPPORT_GACHA_RARITY = () => gameData?.SUPPORT_GACHA_RARITY || {};

// Events
export const getRANDOM_EVENTS = () => gameData?.RANDOM_EVENTS || [];
export const getHANGOUT_EVENTS = () => gameData?.HANGOUT_EVENTS || [];

// Gacha
export const getGACHA_RARITY = () => gameData?.GACHA_RARITY || {};
export const getGACHA_POOLS_BY_GENERATION = () => gameData?.GACHA_POOLS_BY_GENERATION || {};
export const getGENERATIONS = () => gameData?.GENERATIONS || {};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Normalize support name to handle legacy formats
 */
export const normalizeSupportName = (name) => {
  if (!name) return name;
  const legacyMap = getLEGACY_SUPPORT_NAME_MAP();
  return legacyMap[name] || name;
};

/**
 * Get support card with limit break bonuses applied
 * Matches backend logic in supports.js getSupportAtLimitBreak
 */
export const getSupportAtLimitBreak = (supportName, limitBreakLevel = 4) => {
  const supportCards = getSUPPORT_CARDS();
  const progressions = getSUPPORT_LIMIT_BREAK_PROGRESSIONS();

  const normalizedName = normalizeSupportName(supportName);
  const baseCard = supportCards[normalizedName];
  if (!baseCard) return null;

  // Clamp limit break level
  const lb = Math.max(0, Math.min(4, limitBreakLevel));

  // If at max LB or no progression defined, return base card
  const progression = progressions[normalizedName];
  if (lb === 4 || !progression) return baseCard;

  const lbData = progression.progression?.[lb];
  if (!lbData) return baseCard;

  // Apply multipliers to base stats
  const adjustedBaseStats = {};
  if (baseCard.baseStats) {
    for (const [stat, value] of Object.entries(baseCard.baseStats)) {
      adjustedBaseStats[stat] = Math.floor(value * lbData.baseStats);
    }
  }

  // Apply multipliers to training bonuses
  const adjustedTrainingBonus = baseCard.trainingBonus ? {
    typeMatch: Math.floor(baseCard.trainingBonus.typeMatch * lbData.trainingBonus),
    otherStats: Math.max(1, Math.floor(baseCard.trainingBonus.otherStats * lbData.trainingBonus)),
    maxFriendshipTypeMatch: Math.floor(baseCard.trainingBonus.maxFriendshipTypeMatch * lbData.trainingBonus)
  } : null;

  // Handle special effects
  let adjustedSpecialEffect = null;
  if (baseCard.specialEffect && lbData.specialEffect > 0) {
    adjustedSpecialEffect = {};
    const integerBonusEffects = ['maxEnergyBonus', 'restBonus', 'friendshipGainBonus', 'energyRegenBonus', 'energyCostReduction'];

    for (const [effect, value] of Object.entries(baseCard.specialEffect)) {
      if (typeof value === 'number') {
        if (effect.includes('Multiplier')) {
          const bonus = value - 1.0;
          adjustedSpecialEffect[effect] = 1.0 + (bonus * lbData.specialEffect);
        } else if (integerBonusEffects.includes(effect)) {
          adjustedSpecialEffect[effect] = Math.round(value * lbData.specialEffect);
        } else {
          adjustedSpecialEffect[effect] = value * lbData.specialEffect;
        }
      } else {
        adjustedSpecialEffect[effect] = value;
      }
    }

    // Check if any effect is meaningful
    const hasSignificantEffect = Object.entries(adjustedSpecialEffect).some(([key, val]) => {
      if (key.includes('Multiplier')) return val > 1.005;
      if (integerBonusEffects.includes(key)) return val > 0;
      return val > 0.001;
    });
    if (!hasSignificantEffect) adjustedSpecialEffect = null;
  }

  return {
    ...baseCard,
    baseStats: adjustedBaseStats,
    trainingBonus: adjustedTrainingBonus,
    appearanceRate: lbData.appearanceRate,
    typeMatchPreference: lbData.typeMatchPreference,
    initialFriendship: lbData.initialFriendship,
    specialEffect: adjustedSpecialEffect,
    _limitBreakLevel: lb,
    _isMaxLimitBreak: false
  };
};

/**
 * Calculate base stats with evolution adjustment
 */
export const calculateBaseStats = (rawStats, evolutionStages) => {
  const config = getEVOLUTION_CONFIG();
  if (!config.BASE_STAT_MULTIPLIERS) return rawStats;

  let multiplier = config.BASE_STAT_MULTIPLIERS.TWO_EVOLUTIONS;

  if (evolutionStages === 0) {
    multiplier = config.BASE_STAT_MULTIPLIERS.NO_EVOLUTION;
  } else if (evolutionStages === 1) {
    multiplier = config.BASE_STAT_MULTIPLIERS.ONE_EVOLUTION;
  }

  const adjustedStats = {};
  for (const [stat, value] of Object.entries(rawStats)) {
    adjustedStats[stat] = Math.round(value * multiplier);
  }

  const total = Object.values(adjustedStats).reduce((sum, val) => sum + val, 0);
  if (total < 300) {
    const scale = 300 / total;
    for (const stat in adjustedStats) {
      adjustedStats[stat] = Math.round(adjustedStats[stat] * scale);
    }
  } else if (total > 400) {
    const scale = 400 / total;
    for (const stat in adjustedStats) {
      adjustedStats[stat] = Math.round(adjustedStats[stat] * scale);
    }
  }

  return adjustedStats;
};
